/**
 * Clerk · Issue Components
 * Lists approved requests waiting to be issued.
 * Actions: [ ISSUE ] (with confirm) · [ REJECT ] (with reason).
 * Reuses:
 *   GET  /api/clerk/pending-issuance
 *   PUT  /api/orders/{id}/issue
 *   PUT  /api/orders/{id}/clerk-reject   { reason }
 */
import React, { useMemo, useState } from 'react';
import { Check, X, ChevronRight, Inbox, Image as ImageIcon } from 'lucide-react';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import {
  Card, EmptyState, StatusPill, SearchInput, Modal,
  formatShortTime, shortRef,
} from '../ui';

const API_ORIGIN = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const photoUrl = (path) => (path && path.startsWith('http') ? path : `${API_ORIGIN}/api${path}`);

export default function IssuePage({ ctx }) {
  const { pendingIssuance, refresh, showToast } = ctx;
  const [query, setQuery] = useState('');

  // Modal state
  const [confirmOrder, setConfirmOrder] = useState(null);
  const [issuing, setIssuing] = useState(false);

  const [rejectOrder, setRejectOrder] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  // "Last cleaned photo" peek — keyed by component_id inside the confirm
  // modal. Nothing is cached across orders; each peek re-fetches so clerks
  // always see the freshest cleaned batch.
  const [peekItem, setPeekItem] = useState(null); // { component_id, component_name }
  const [peekLoading, setPeekLoading] = useState(false);
  const [peekData, setPeekData] = useState(null);  // { count, returns:[...] }

  const openPeek = async (item) => {
    setPeekItem({ component_id: item.component_id, component_name: item.component_name });
    setPeekData(null);
    setPeekLoading(true);
    try {
      const { data } = await apiClient.get(endpoints.components.recentReturnPhotos(item.component_id));
      setPeekData(data || { count: 0, returns: [] });
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Could not load cleaned photos.', 'bad');
      setPeekData({ count: 0, returns: [] });
    } finally {
      setPeekLoading(false);
    }
  };
  const closePeek = () => { setPeekItem(null); setPeekData(null); };

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return pendingIssuance;
    return pendingIssuance.filter((o) => {
      const hay = [
        o.student_name, o.patient_name, o.patient_pid, o.id, o.approver_name,
        ...(o.items || []).flatMap((it) => [it.component_name, it.brand_name]),
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [pendingIssuance, query]);

  const openConfirm = (order) => setConfirmOrder(order);
  const openReject  = (order) => { setRejectOrder(order); setRejectReason(''); };

  const doIssue = async () => {
    if (!confirmOrder || issuing) return;
    setIssuing(true);
    try {
      await apiClient.put(endpoints.orders.issue(confirmOrder.id));
      showToast(`Issued ${shortRef(confirmOrder.id, 'REQ')} · ${confirmOrder.student_name}`, 'ok');
      setConfirmOrder(null);
      await refresh.issuance();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Issue failed. Refreshing queue.', 'bad');
      await refresh.issuance();
    } finally {
      setIssuing(false);
    }
  };

  const doReject = async () => {
    if (!rejectOrder || rejecting) return;
    const reason = rejectReason.trim();
    if (!reason) {
      showToast('Please add a short reason for rejection.', 'bad');
      return;
    }
    setRejecting(true);
    try {
      await apiClient.put(endpoints.orders.clerkReject(rejectOrder.id), { reason });
      showToast(`Rejected ${shortRef(rejectOrder.id, 'REQ')} · stock restored`, 'ok');
      setRejectOrder(null);
      setRejectReason('');
      await refresh.issuance();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Reject failed.', 'bad');
    } finally {
      setRejecting(false);
    }
  };

  const totalQty = (order) => (order?.items || []).reduce((s, it) => s + (it.quantity || 0), 0);

  return (
    <div data-testid="cp-issue-page">
      <div className="cp-eyebrow">Operational Queue</div>
      <h1 className="cp-page-title">Issue Components</h1>
      <p className="cp-page-lede">
        Approved by faculty and awaiting physical hand-off. Confirming an issue records
        the transaction and updates the student's kit immediately.
      </p>

      <div className="cp-toolbar cp-mt-5">
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="Search by student, request ID, patient or component…"
          testId="cp-issue-search"
        />
        <div className="cp-subtle">
          {rows.length} {rows.length === 1 ? 'request' : 'requests'}
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="No approved requests waiting"
          message="When faculty approves a request it will appear here for hand-off."
          testId="cp-issue-empty"
        />
      ) : (
        <div className="cp-table-wrap" data-testid="cp-issue-table">
          <table className="cp-table">
            <thead>
              <tr>
                <th style={{ width: '20%' }}>Student</th>
                <th style={{ width: '18%' }}>Request</th>
                <th>Components</th>
                <th style={{ width: '14%' }}>Approval</th>
                <th style={{ width: '18%' }} className="cp-td-actions">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id} data-testid={`cp-issue-row-${o.id}`}>
                  <td>
                    <div className="cp-td-strong">{o.student_name || '—'}</div>
                    <div className="cp-td-meta">
                      {o.patient_name || 'No patient info'}
                      {o.patient_pid && <span> · PID {o.patient_pid}</span>}
                    </div>
                  </td>
                  <td>
                    <div className="cp-td-mono">{shortRef(o.id, 'REQ')}</div>
                    <div className="cp-td-meta">Total qty · {totalQty(o)}</div>
                  </td>
                  <td>
                    <ul className="cp-item-lines">
                      {(o.items || []).map((it, i) => (
                        <li key={i}>
                          <span className="cp-item-qty">×{it.quantity}</span>
                          <strong>{it.component_name}</strong>
                          {it.brand_name && <span className="cp-item-brand">{it.brand_name}</span>}
                          {it.is_returnable && (
                            <span style={{ marginLeft: 4 }}>
                              <StatusPill variant="returnable">Returnable</StatusPill>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td>
                    <div className="cp-td-meta cp-td-strong" style={{ marginTop: 0 }}>
                      {o.approver_name || 'Faculty'}
                    </div>
                    <div className="cp-td-meta">{o.formatted_approved_at || formatShortTime(o.approved_at)}</div>
                  </td>
                  <td className="cp-td-actions">
                    <button
                      type="button"
                      className="cp-btn cp-btn-ghost cp-btn-sm"
                      onClick={() => openReject(o)}
                      data-testid={`cp-reject-btn-${o.id}`}
                      title="Reject and restore reserved stock"
                    >
                      <X size={14} /> Reject
                    </button>
                    <button
                      type="button"
                      className="cp-btn cp-btn-accent cp-btn-sm"
                      style={{ marginLeft: 8 }}
                      onClick={() => openConfirm(o)}
                      data-testid={`cp-issue-btn-${o.id}`}
                    >
                      Issue <ChevronRight size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Issue confirmation */}
      <Modal
        open={!!confirmOrder}
        onClose={() => !issuing && setConfirmOrder(null)}
        title="Issue Component"
        subtitle="Confirm you are physically handing over the following."
        testId="cp-issue-confirm-modal"
        footer={(
          <>
            <button
              type="button"
              className="cp-btn cp-btn-ghost"
              onClick={() => setConfirmOrder(null)}
              disabled={issuing}
              data-testid="cp-issue-confirm-cancel"
            >Cancel</button>
            <button
              type="button"
              className="cp-btn cp-btn-accent"
              onClick={doIssue}
              disabled={issuing}
              data-testid="cp-issue-confirm-submit"
            >
              <Check size={15} /> {issuing ? 'Issuing…' : 'Confirm Issue'}
            </button>
          </>
        )}
      >
        {confirmOrder && (
          <>
            <dl className="cp-def-list">
              <div>
                <dt>Student</dt>
                <dd>{confirmOrder.student_name || '—'}</dd>
              </div>
              <div>
                <dt>Request</dt>
                <dd className="cp-mono-num">{shortRef(confirmOrder.id, 'REQ')}</dd>
              </div>
              <div>
                <dt>Patient</dt>
                <dd>{confirmOrder.patient_name || '—'}{confirmOrder.patient_pid ? ` · PID ${confirmOrder.patient_pid}` : ''}</dd>
              </div>
              <div>
                <dt>Total Quantity</dt>
                <dd className="cp-mono-num">{totalQty(confirmOrder)}</dd>
              </div>
            </dl>

            <div className="cp-mt-5">
              <div className="cp-field-label">Components</div>
              <ul className="cp-item-lines" style={{ gap: 8 }}>
                {(confirmOrder.items || []).map((it, i) => {
                  const fromReissuable = Number(it.from_reissuable || 0);
                  return (
                    <li key={i}>
                      <span className="cp-item-qty">×{it.quantity}</span>
                      <strong>{it.component_name}</strong>
                      {it.brand_name && <span className="cp-item-brand">{it.brand_name}</span>}
                      {it.is_returnable && (
                        <span style={{ marginLeft: 6 }}><StatusPill variant="returnable">Returnable</StatusPill></span>
                      )}
                      {fromReissuable > 0 && (
                        <span style={{ marginLeft: 6 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: 'rgba(37, 99, 235, 0.10)', color: '#1D4ED8' }}>
                            {fromReissuable} from re-issuable
                          </span>
                          <button
                            type="button"
                            onClick={() => openPeek(it)}
                            data-testid={`cp-issue-peek-photo-${it.component_id}`}
                            title="View the most-recent cleaned photo for this component"
                            style={{ marginLeft: 6, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '1px 8px', borderRadius: 6, border: '1px solid var(--cp-border, #d1d5db)', background: 'white', fontSize: 11, cursor: 'pointer' }}
                          >
                            <ImageIcon size={11} /> View last cleaned photo
                          </button>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>

            <p className="cp-mt-4 cp-subtle">
              Reserved stock was drawn at approval time — confirming here simply completes the hand-off.
            </p>
          </>
        )}
      </Modal>

      {/* Last-cleaned-photo peek — fired from the issue confirm modal for
          items sourced from the reissuable pool. */}
      <Modal
        open={!!peekItem}
        onClose={closePeek}
        title={peekItem ? `Last cleaned photo · ${peekItem.component_name}` : ''}
        subtitle="Most-recent approved return photos for this component. Confirms the physical unit you are handing over matches the cleaned batch."
        testId="cp-issue-peek-modal"
        footer={(
          <button
            type="button"
            className="cp-btn cp-btn-ghost"
            onClick={closePeek}
            data-testid="cp-issue-peek-close"
          >Close</button>
        )}
      >
        {peekLoading && <div style={{ padding: 24, textAlign: 'center' }}>Loading…</div>}
        {!peekLoading && peekData && peekData.count === 0 && (
          <div data-testid="cp-issue-peek-empty" style={{ padding: '16px 0', color: 'var(--cp-ink-muted, #6b7280)', fontSize: 14 }}>
            No cleaned photos on record for this component yet — this batch may be freshly issued
            for the first time from the re-issuable pool.
          </div>
        )}
        {!peekLoading && peekData && peekData.count > 0 && (
          <div data-testid="cp-issue-peek-gallery">
            {peekData.returns.map((r) => (
              <div key={r.id} style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--cp-ink-muted, #6b7280)', marginBottom: 8 }}>
                  <span>Cleaned by <strong>{r.clerk_verified_by_name || 'clerk'}</strong></span>
                  <span>{r.clerk_verified_at ? formatShortTime(r.clerk_verified_at) : ''}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
                  {(r.clerk_verification_photos || []).map((p, i) => (
                    <a
                      key={i}
                      href={photoUrl(p)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`cp-issue-peek-photo-link-${r.id}-${i}`}
                      style={{ display: 'block', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#f9fafb' }}
                    >
                      <img
                        src={photoUrl(p)}
                        alt="cleaned component"
                        loading="lazy"
                        style={{ display: 'block', width: '100%', height: 120, objectFit: 'cover' }}
                      />
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
      <Modal
        open={!!rejectOrder}
        onClose={() => !rejecting && setRejectOrder(null)}
        title="Reject Request"
        subtitle="Restore the reserved stock and notify the student's faculty."
        testId="cp-reject-modal"
        footer={(
          <>
            <button
              type="button"
              className="cp-btn cp-btn-ghost"
              onClick={() => setRejectOrder(null)}
              disabled={rejecting}
              data-testid="cp-reject-cancel"
            >Cancel</button>
            <button
              type="button"
              className="cp-btn cp-btn-danger"
              onClick={doReject}
              disabled={rejecting || !rejectReason.trim()}
              data-testid="cp-reject-submit"
            >
              {rejecting ? 'Rejecting…' : 'Confirm Reject'}
            </button>
          </>
        )}
      >
        {rejectOrder && (
          <>
            <dl className="cp-def-list">
              <div>
                <dt>Student</dt><dd>{rejectOrder.student_name}</dd>
              </div>
              <div>
                <dt>Request</dt><dd className="cp-mono-num">{shortRef(rejectOrder.id, 'REQ')}</dd>
              </div>
            </dl>
            <div className="cp-mt-5">
              <label className="cp-field-label" htmlFor="cp-reject-reason">Reason</label>
              <textarea
                id="cp-reject-reason"
                className="cp-textarea"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="e.g. Component damaged, out of usable stock, mismatched size…"
                data-testid="cp-reject-reason"
                autoFocus
              />
            </div>
            <div className="cp-alert cp-mt-4">
              Reserved stock for every item in this request will be restored to the correct pool
              (fresh or re-issuable) automatically.
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
